import React, { Fragment, useEffect, useState, useRef } from 'react';

import { Box, Typography, Button, Chip } from '@mui/material';
import Grid from '@mui/material/Grid2';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import api from 'utils/api';
import useApi from 'shared/hooks/api';
import localData from 'utils/localData';

// constants
import { DOLPHIN_API } from 'shared/constants/apis';

// components and parts
import { PageLoader, PageError } from 'components';
import { USER_DATA } from 'shared/constants/users';

// Alert severity values
const ALERT_SEVERITY = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
};

// Dialog types
const DIALOG_TYPES = {
  SAVE: 'save',
  RESTORE: 'restore',
};

const propTypes = {};

const AdminLLMPrompts = () => {
  const [promptData, setPromptData] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState(DIALOG_TYPES.SAVE);
  const [editedContent, setEditedContent] = useState({});
  const [language, setLanguage] = useState('en-US');
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: ALERT_SEVERITY.SUCCESS,
  });
  // Create refs for each prompt card
  const promptRefs = useRef({});

  const [{ data, error }, fetchPrompts] = useApi.useGet(
    DOLPHIN_API.NARWHAL_PROMPTS,
    {
      params: {
        username: localData.get(USER_DATA.USERNAME),
        language: language,
      },
      baseURL: import.meta.env.REACT_APP_API_URL,
    }
  );

  useEffect(() => {
    if (data) {
      setPromptData(data);
      // Initialize editedContent with the original content
      const contentMap = {};
      data.forEach((prompt) => {
        contentMap[prompt.id] = prompt.content;
      });
      setEditedContent(contentMap);

      // Initialize refs for each prompt
      promptRefs.current = data.reduce((acc, prompt) => {
        acc[prompt.id] = React.createRef();
        return acc;
      }, {});
    }
  }, [data]);

  // Add effect to refetch data when language changes
  useEffect(() => {
    fetchPrompts();
  }, [language, fetchPrompts]);

  const handleLanguageChange = (event) => {
    setLanguage(event.target.value);
  };

  const handleContentChange = (id, newContent) => {
    setEditedContent((prev) => ({
      ...prev,
      [id]: newContent,
    }));
  };

  const handleSaveClick = (prompt) => {
    // Create an updated prompt object with the latest edited content
    const updatedPrompt = {
      ...prompt,
      content: editedContent[prompt.id] || prompt.content,
    };

    setSelectedPrompt(updatedPrompt);
    setDialogType(DIALOG_TYPES.SAVE);
    setOpenDialog(true);
  };

  const handleRestoreClick = (prompt) => {
    // Create an updated prompt object with the latest edited content
    const updatedPrompt = {
      ...prompt,
      content: editedContent[prompt.id] || prompt.content,
    };
    setSelectedPrompt(updatedPrompt);
    setDialogType(DIALOG_TYPES.RESTORE);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleConfirmSave = async () => {
    if (!selectedPrompt) return;
    setIsLoading(true);
    try {
      // Send PUT request to update the prompt
      const response = await api.put(
        `${DOLPHIN_API.NARWHAL_PROMPTS}/${selectedPrompt.id}`,
        {
          params: {
            username: localData.get(USER_DATA.USERNAME),
            language: language,
          },
          data: {
            content: selectedPrompt.content,
          },
        }
      );

      // If successful, update local state
      if (response) {
        const updatedPrompts = promptData.map((p) => {
          if (p.id === selectedPrompt.id) {
            return {
              ...p,
              content: editedContent[p.id],
              updatedAt: new Date().toISOString(),
            };
          }
          return p;
        });

        setPromptData(updatedPrompts);
        // Also update the selectedPrompt to keep it in sync with promptData
        setSelectedPrompt((prev) => {
          if (prev) {
            return {
              ...prev,
              content: editedContent[prev.id],
              updatedAt: new Date().toISOString(),
            };
          }
          return prev;
        });

        setSnackbar({
          open: true,
          message: 'Prompt updated successfully',
          severity: ALERT_SEVERITY.SUCCESS,
        });
      }
    } catch (error) {
      console.error('Error saving prompt:', error);
      setSnackbar({
        open: true,
        message: `Failed to update prompt: ${error.message || 'Unknown error'}`,
        severity: ALERT_SEVERITY.ERROR,
      });
    } finally {
      setIsLoading(false);
      setOpenDialog(false);
    }
  };

  const handleConfirmRestore = async () => {
    if (!selectedPrompt) return;

    setIsLoading(true);
    try {
      // Send PUT request to restore the prompt to default
      const response = await api.put(
        `${DOLPHIN_API.NARWHAL_PROMPTS_RESTORE_DEFAULT}/${selectedPrompt.id}`,
        {
          params: {
            username: localData.get(USER_DATA.USERNAME),
            language: language,
          },
        }
      );
      // If successful, update local state
      if (response) {
        try {
          // Fetch the specific prompt to get the updated content
          const promptResponse = await api.get(
            `${DOLPHIN_API.NARWHAL_PROMPTS}/${selectedPrompt.id}`,
            {
              params: {
                username: localData.get(USER_DATA.USERNAME),
                language: language,
              },
            }
          );

          // Update the editedContent with the restored default content
          if (promptResponse.data && promptResponse.data.content) {
            setEditedContent((prev) => ({
              ...prev,
              [selectedPrompt.id]: promptResponse.data.content,
            }));
          }
        } catch (fetchError) {
          console.error('Error fetching restored prompt:', fetchError);
        }

        // Refresh the full list of prompts
        fetchPrompts();

        setSnackbar({
          open: true,
          message: 'Prompt restored to default successfully',
          severity: ALERT_SEVERITY.SUCCESS,
        });
      }
    } catch (error) {
      console.error('Error restoring prompt:', error);
      setSnackbar({
        open: true,
        message: `Failed to restore prompt: ${
          error.message || 'Unknown error'
        }`,
        severity: ALERT_SEVERITY.ERROR,
      });
    } finally {
      setIsLoading(false);
      setOpenDialog(false);
    }
  };

  const handleConfirmAction = () => {
    if (dialogType === DIALOG_TYPES.SAVE) {
      handleConfirmSave();
    } else if (dialogType === DIALOG_TYPES.RESTORE) {
      handleConfirmRestore();
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);

    // Format date and time manually
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const year = date.getFullYear();
    const month = months[date.getMonth()];
    const day = date.getDate();

    // Format time with AM/PM
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // Convert 0 to 12

    return `${month} ${day}, ${year} at ${hours}:${minutes} ${ampm}`;
  };

  // Function to scroll to a specific prompt card
  const scrollToPrompt = (id) => {
    if (promptRefs.current[id] && promptRefs.current[id].current) {
      promptRefs.current[id].current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  if (!data) return <PageLoader />;
  if (error)
    return (
      <PageError
        errorCode={error.response.status}
        customErrorMessage={error.response.data.message}
        customErrorDescription={error.response.data.description}
      />
    );

  return (
    <Fragment>
      <Grid container style={{ marginBottom: '10px', marginTop: '10px' }}>
        <Grid item xs={12}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box ml={4}>
              <Typography
                variant="h3"
                style={{
                  fontWeight: 'bold',
                  marginTop: '16px',
                  marginRight: '8px',
                  textAlign: 'right',
                  alignSelf: 'stretch',
                  display: 'inline-block',
                }}>
                {`All Prompts: `}
              </Typography>
            </Box>
            <FormControl
              variant="outlined"
              style={{ minWidth: 200, marginTop: '16px' }}>
              <InputLabel id="language-select-label">Language</InputLabel>
              <Select
                labelId="language-select-label"
                id="language-select"
                value={language}
                onChange={handleLanguageChange}
                label="Language">
                <MenuItem value="en-US">English (US)</MenuItem>
                <MenuItem value="ja-JP">Japanese</MenuItem>
                <MenuItem value="ko-KR">Korean</MenuItem>
                <MenuItem value="id-ID">Indonesian</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Grid>
      </Grid>

      {/* Navigation list */}
      <Box sx={{ padding: '0 24px 20px 24px' }}>
        <Typography variant="h6" gutterBottom>
          Quick Navigation:
        </Typography>
        <Grid container spacing={3}>
          {/* First Column */}
          <Grid item xs={6}>
            <Typography variant="subtitle1" gutterBottom>
              Prompts (1/2)
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {promptData
                .slice(0, Math.ceil(promptData.length / 2))
                .map((prompt) => (
                  <Chip
                    key={`nav-${prompt.id}`}
                    label={prompt.name}
                    onClick={() => scrollToPrompt(prompt.id)}
                    clickable
                    color="primary"
                    variant="outlined"
                    style={{ margin: '4px' }}
                  />
                ))}
            </Box>
          </Grid>

          {/* Second Column */}
          <Grid item xs={6}>
            <Typography variant="subtitle1" gutterBottom>
              Prompts (2/2)
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {promptData
                .slice(Math.ceil(promptData.length / 2))
                .map((prompt) => (
                  <Chip
                    key={`nav-${prompt.id}`}
                    label={prompt.name}
                    onClick={() => scrollToPrompt(prompt.id)}
                    clickable
                    color="primary"
                    variant="outlined"
                    style={{ margin: '4px' }}
                  />
                ))}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ padding: 3 }}>
        {promptData.map((prompt) => (
          <Card
            key={prompt.id}
            sx={{ marginBottom: 3, overflow: 'visible' }}
            ref={promptRefs.current[prompt.id]}
            id={`prompt-card-${prompt.id}`}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography variant="h5" component="div">
                  {prompt.name}
                </Typography>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-end">
                  <Typography variant="body2" color="textSecondary">
                    Created: {formatDate(prompt.createdAt)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Last Updated: {formatDate(prompt.updatedAt)}
                  </Typography>
                </Box>
              </Box>

              <div style={{ margin: '16px 0' }}>
                <textarea
                  style={{
                    minHeight: '40px',
                    maxHeight: '800px',
                    width: '100%',
                    resize: 'both',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    fontFamily: 'inherit',
                    fontSize: '1rem',
                    overflow: 'auto',
                  }}
                  value={editedContent[prompt.id] || ''}
                  onChange={(e) =>
                    handleContentChange(prompt.id, e.target.value)
                  }
                  rows={Math.min(
                    20,
                    Math.max(
                      4,
                      (editedContent[prompt.id] || '').split('\n').length + 2
                    )
                  )}
                />
              </div>

              <Box display="flex" justifyContent="space-between" mt={2}>
                <Button
                  variant="contained"
                  color="primary"
                  style={{ color: '#fff' }}
                  onClick={() => handleRestoreClick(prompt)}
                  disabled={
                    isLoading &&
                    selectedPrompt &&
                    selectedPrompt.id === prompt.id
                  }>
                  {isLoading &&
                  selectedPrompt &&
                  selectedPrompt.id === prompt.id &&
                  dialogType === DIALOG_TYPES.RESTORE
                    ? 'Restoring...'
                    : 'Restore to Default'}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  style={{ color: '#fff' }}
                  onClick={() => handleSaveClick(prompt)}
                  disabled={
                    isLoading &&
                    selectedPrompt &&
                    selectedPrompt.id === prompt.id
                  }>
                  {isLoading &&
                  selectedPrompt &&
                  selectedPrompt.id === prompt.id &&
                  dialogType === DIALOG_TYPES.SAVE
                    ? 'Saving...'
                    : 'Save'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">
          {dialogType === DIALOG_TYPES.SAVE
            ? 'Confirm Prompt Update'
            : 'Confirm Restore to Default'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {dialogType === DIALOG_TYPES.SAVE
              ? 'Are you sure you want to do this modification?'
              : 'Are you sure you want to restore this prompt to its default value? This action cannot be undone.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDialogClose}
            color="primary"
            disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            color="primary"
            autoFocus
            disabled={isLoading}>
            {isLoading
              ? dialogType === DIALOG_TYPES.SAVE
                ? 'Saving...'
                : 'Restoring...'
              : 'Yes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}>
        <Alert
          onClose={handleCloseSnackbar}
          severity={
            snackbar.severity === 'success'
              ? 'success'
              : snackbar.severity === 'error'
              ? 'error'
              : snackbar.severity === 'warning'
              ? 'warning'
              : 'info'
          }>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Fragment>
  );
};

AdminLLMPrompts.propTypes = propTypes;

export default AdminLLMPrompts;
